import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ScrollingText from './ScrollingText';
import createDataURL from '../utils/createDataURL';
import ReadOnlyStars from './ReadOnlyStars';

const HomePage = ({ accessToken, currentUser }) => {
  const [error, setError] = useState('');
  const [shuffledRecommendations, setShuffledRecommendations] = useState(null);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(true);
  const [reviewSummaries, setReviewSummaries] = useState({}); // { [trackId]: { avgRating, count } }

  const [latestReviews, setLatestReviews] = useState([]);
  const [topReviews, setTopReviews] = useState([]);
  const [topRange, setTopRange] = useState('all');
  const [newReleases, setNewReleases] = useState([]);
  const [newReleasesB, setNewReleasesB] = useState([]);
  const [isLoadingNewReleases, setIsLoadingNewReleases] = useState(true);

  const [isLoadingLatest, setIsLoadingLatest] = useState(true);
  const [isLoadingTop, setIsLoadingTop] = useState(true);

  // ---- Horizontal scroller state ----
  const scrollerRef = useRef(null);
  const [snapX, setSnapX] = useState([]); // pixel positions of each page
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);




  // Helper: nearest index to a given scrollLeft
  const getNearestIndex = (positions, curr) => {
    if (!positions || positions.length === 0) return 0;
    let best = 0;
    let bestDist = Math.abs(positions[0] - curr);
    for (let i = 1; i < positions.length; i++) {
      const d = Math.abs(positions[i] - curr);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  };

  // Current page based on snapX + scrollLeft
  const getCurrentPageIdx = () => {
    const el = scrollerRef.current;
    if (!el || snapX.length === 0) return 0;
    return getNearestIndex(snapX, el.scrollLeft);
  };

  // Compute snap points (page offsets) and set arrow state from fresh positions
  const computeSnapPoints = () => {
    const el = scrollerRef.current;
    if (!el) return;

    const pages = Array.from(el.children);
    if (pages.length === 0) {
      setSnapX([]);
      setCanPrev(false);
      setCanNext(false);
      return;
    }

    // Normalize so the first page is position 0
    const firstLeft = pages[0].offsetLeft;
    const positions = pages.map(p => p.offsetLeft - firstLeft);

    setSnapX(positions);

    const curr = el.scrollLeft;
    const idx = getNearestIndex(positions, curr);
    setCanPrev(idx > 0);
    setCanNext(idx < positions.length - 1);
  };

  // Update arrows whenever the user scrolls
  const updateArrowState = () => {
    const el = scrollerRef.current;
    if (!el || snapX.length === 0) { setCanPrev(false); setCanNext(false); return; }
    const idx = getNearestIndex(snapX, el.scrollLeft);
    setCanPrev(idx > 0);
    setCanNext(idx < snapX.length - 2);
  };

  // Index-based scrolling (reliable with scroll snapping)
  const scrollToIndex = (idx) => {
    const el = scrollerRef.current;
    if (!el || snapX.length === 0) return;
    const clamped = Math.max(0, Math.min(idx, snapX.length - 1));
    el.scrollTo({ left: snapX[clamped], behavior: 'smooth' });
  };

  const scrollToNext = () => scrollToIndex(getCurrentPageIdx() + 1);
  const scrollToPrev = () => scrollToIndex(getCurrentPageIdx() - 1);

  // Recompute pages after recommendations render
  useEffect(() => {
    const id = requestAnimationFrame(computeSnapPoints);
    return () => cancelAnimationFrame(id);
  }, [shuffledRecommendations]);

  // Recompute on resize (cards can reflow)
  useEffect(() => {
    const onResize = () => computeSnapPoints();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ---- Data fetching ----
  useEffect(() => {
    if (currentUser) {
      setIsLoadingRecommendations(true);
      fetchRecommendations();
    }
  }, [currentUser]);

  const fetchRecommendations = async () => {
    try {
      setError('');
      setIsLoadingRecommendations(true);
      setShuffledRecommendations(null);

      const response = await fetch('/api/recommendations/spotify', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setShuffledRecommendations(data);
      } else {
        console.error('Failed to fetch recommendations:', response.status);
        setShuffledRecommendations([]);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setShuffledRecommendations([]);
    } finally {
      setIsLoadingRecommendations(false);
    }
  };

  // Fetch average ratings for shown tracks
  useEffect(() => {
    if (!shuffledRecommendations || shuffledRecommendations.length === 0) return;
    let alive = true;

    const ids = Array.from(new Set(shuffledRecommendations.map(t => t.id))).filter(Boolean);
    const limited = ids.slice(0, 24);

    Promise.allSettled(
      limited.map(id =>
        fetch(`/api/reviews/summary?type=track&id=${encodeURIComponent(id)}`)
          .then(r => (r.ok ? r.json() : Promise.reject()))
          .then(data => ({ id, data }))
          .catch(() => ({ id, data: { avgRating: null, count: 0 } }))
      )
    )
      .then(results => {
        if (!alive) return;
        const next = {};
        results.forEach(r => {
          if (r.status === 'fulfilled' && r.value) {
            const { id, data } = r.value;
            // Store something for every id so RatingRow has a stable entry
            next[id] = data && typeof data === 'object'
              ? { avgRating: data.avgRating ?? null, count: Number(data.count ?? 0) }
              : { avgRating: null, count: 0 };
          } else if (r.status === 'rejected') {
            // also record a default entry on rejection
            // (we don't know the id here, but we handled it in catch above)
          }
        });
        setReviewSummaries(prev => ({ ...prev, ...next }));
      })
      .catch(() => {})
    
    return () => { alive = false; };
  }, [shuffledRecommendations]);

  // Fetch new releases (always, for default content)
  useEffect(() => {
    const loadNewReleases = async () => {
      try {
        setIsLoadingNewReleases(true);
        const [r1, r2] = await Promise.all([
          fetch('/api/spotify/new-releases?limit=10&offset=0'),
          fetch('/api/spotify/new-releases?limit=10&offset=10'),
        ]);
        const [d1, d2] = await Promise.all([r1.json(), r2.json()]);
        setNewReleases(d1?.albums?.items || []);
        setNewReleasesB(d2?.albums?.items || []);
      } catch (e) {
        // silently fail
      } finally {
        setIsLoadingNewReleases(false);
      }
    };
    loadNewReleases();
  }, []);

  // Fetch Latest Reviews (on page load)
  useEffect(() => {
    const loadLatest = async () => {
      try {
        setIsLoadingLatest(true);
        const r = await fetch('/api/reviews/latest?limit=12');
        setLatestReviews(r.ok ? await r.json() : []);
      } catch (e) {
        setLatestReviews([]);
      } finally {
        setIsLoadingLatest(false);
      }
    };
    loadLatest();
  }, []);

  // Fetch Top Reviews whenever filter changes
  useEffect(() => {
    const loadTop = async () => {
      try {
        setIsLoadingTop(true);
        const r = await fetch(`/api/reviews/top?range=${encodeURIComponent(topRange)}&limit=12`);
        setTopReviews(r.ok ? await r.json() : []);
      } catch (e) {
        setTopReviews([]);
      } finally {
        setIsLoadingTop(false);
      }
    };
    loadTop();
  }, [topRange]);

  const RatingRow = ({ trackId }) => {
    const s = reviewSummaries[trackId];
    if (!s || !s.count || s.avgRating == null) {
      // if no reviews yet
      return (
        <div
          className="truncate-one-line"
          style={{ color: '#666', fontSize: '0.85rem', marginTop: 4, marginBottom: '2px' }}
        >
          No Reviews Yet
        </div>
      );
    }
    return (
      <div className="d-flex align-items-center gap-2 stars--neutral truncate-one-line" style={{ marginTop: 4 }}>
        <ReadOnlyStars value={s.avgRating} ariaLabel={`Average rating ${s.avgRating} out of 5`} />
        <strong style={{ fontSize: '0.9rem' }}>{s.avgRating.toFixed(1)}</strong>
        <span style={{ color: '#666', fontSize: '0.85rem' }}>({s.count})</span>
      </div>
    );
  };

  const renderReviewCard = (review) => {
    const img = review.itemImage || createDataURL('📝', '#cccccc', '#666666', 80, 80);
    const title = review.itemName || 'Untitled';
    const snippet = (review.review || '').slice(0, 140);

    const ups = review.thumbsUpCount ?? (review.thumbsUp?.length ?? 0);
    const downs = review.thumbsDownCount ?? (review.thumbsDown?.length ?? 0);

    const to = `/review/${review._id}`;

    return (
      <Link
        to={to}
        key={review._id}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block', width: '100%' }}
      >
        <Card
          style={{
            width: '100%',              // ✅ fill the scroller width
            border: 'none',
            background: '#eae9e9ff',
          }}
        >
          <Card.Body style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <img
              src={img}
              alt=""
              style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
              onError={(e) => (e.currentTarget.src = createDataURL('📝', '#cccccc', '#666666', 80, 80))}
            />
            <div style={{ minWidth: 0, flex: 1 }}> {/* ✅ let text column expand */}
              <div className="truncate-one-line" style={{ fontWeight: 700, fontSize: '1rem' }}>
                {title}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: 6, overflow: 'hidden' }}>
                {snippet || 'No text'}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>👍 {ups}</div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>👎 {downs}</div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Link>
    );
  };

  const renderAlbumCard = (album) => {
    const img = album?.images?.[0]?.url || createDataURL('💿', '#cccccc', '#666666', 80, 80);
    const title = album.name || 'Untitled';
    const artists = (album.artists || []).map(a => a.name).join(', ');
    const releaseYear = album.release_date ? album.release_date.slice(0, 4) : '';

    return (
      <Link
        to={`/album/${album.id}`}
        key={album.id}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block', width: '100%' }}
      >
        <Card style={{ width: '100%', border: 'none', background: '#eae9e9ff' }}>
          <Card.Body style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <img
              src={img}
              alt=""
              style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
              onError={(e) => (e.currentTarget.src = createDataURL('💿', '#cccccc', '#666666', 80, 80))}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="truncate-one-line" style={{ fontWeight: 700, fontSize: '1rem' }}>{title}</div>
              <div className="truncate-one-line" style={{ fontSize: '0.85rem', color: '#666', marginTop: 4 }}>{artists}</div>
              {releaseYear && (
                <div style={{ fontSize: '0.8rem', color: '#999', marginTop: 4 }}>{releaseYear}</div>
              )}
            </div>
          </Card.Body>
        </Card>
      </Link>
    );
  };

  // ---- Renderers ----
  const renderTopRecommendation = (track) => {
    const img = track?.album?.images?.[0]?.url || createDataURL('🎵', '#cccccc', '#666666', 227, 227);
    const title = track.name;
    const subtitle = track.artists ? track.artists.map(a => a.name).join(', ') : '';
    const to = `/track/${track.id}`;

    return (
      <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
        <Card
          className="songs-and-top-result-card"
          style={{
            width: '100%',
            height: '285px',
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden',
            backgroundColor: '#eae9e9ff',
            border: 'none',
          }}
        >
          <Card.Img
            className="card-img-top"
            src={img}
            style={{ width: '285px', height: '100%', objectFit: 'cover', flexShrink: 0 }}
            onError={(e) => e.target.src = createDataURL('🎵', '#cccccc', '#666666', 227, 227)}
          />
          <Card.Body
            className="card-body--recommendation"
            style={{
              display: 'flex', flexDirection: 'column', padding: '0.5rem',
              justifyContent: 'center', textAlign: 'left', overflow: 'hidden', minWidth: 0,
            }}
          >
            <Card.Title style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem', minWidth: 0, overflow: 'hidden' }}>
              <ScrollingText>{title}</ScrollingText>
            </Card.Title>
            {subtitle && (
              <Card.Text style={{ fontSize: '1rem', color: '#666', margin: 0, minWidth: 0, overflow: 'hidden' }}>
                <ScrollingText>{subtitle}</ScrollingText>
              </Card.Text>
            )}
            <RatingRow trackId={track.id} />
          </Card.Body>
        </Card>
      </Link>
    );
  };

  const renderSmallRecommendation = (track) => {
    const img = track?.album?.images?.[0]?.url || createDataURL('🎵', '#cccccc', '#666666', 60, 60);
    const title = track.name;
    const subtitle = track.artists ? track.artists.map(a => a.name).join(', ') : '';
    const to = `/track/${track.id}`;

    return (
      <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }} key={track.id}>
        <Card
          className="songs-and-top-result-card"
          style={{
            width: '100%',
            height: '78px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: '0.5rem',
            backgroundColor: '#eae9e9ff',
            border: 'none',
          }}
        >
          <Card.Img
            className="card-img-top"
            src={img}
            style={{ width: '78px', height: '78px', objectFit: 'cover', flexShrink: 0, marginLeft: '0px' }}
            onError={(e) => e.target.src = createDataURL('🎵', '#cccccc', '#666666', 60, 60)}
          />
          <Card.Body
            className="card-body--recommendation"
            style={{
              display: 'flex', flexDirection: 'column', padding: '0.6rem',
              justifyContent: 'center', textAlign: 'left', overflow: 'hidden', minWidth: 0,
            }}
          >
            <Card.Title
              className="truncate-one-line"
              style={{ fontSize: '0.9rem', marginTop: '2px', marginBottom: '0.25rem', minWidth: 0 }}
            >
              <ScrollingText>{title}</ScrollingText>
            </Card.Title>
            {subtitle && (
              <Card.Text
                className="truncate-one-line"
                style={{ fontSize: '0.75rem', color: '#666', marginBottom: 0, minWidth: 0, overflow: 'hidden' }}
              >
                <ScrollingText>{subtitle}</ScrollingText>
              </Card.Text>
            )}
            <RatingRow trackId={track.id} />
          </Card.Body>
        </Card>
      </Link>
    );
  };

  // ---- Render ----
  if (error) {
    return (
      <Container fluid style={{ padding: '20px' }}>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid style={{ padding: '20px' }}>
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={10} xl={10} className="mx-auto">
          {currentUser && (
            <div className="mt-5">
              <h3 style={{ fontWeight: 'bold', marginBottom: '1rem', textAlign: 'left' }}>
                Top Recommendations
              </h3>

              {isLoadingRecommendations || shuffledRecommendations === null ? (
                <div className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading recommendations...</span>
                  </Spinner>
                  <p className="mt-2">Finding music you'll love...</p>
                </div>
              ) : shuffledRecommendations.length === 0 ? (
                <Alert variant="info" className="text-center">
                  <Alert.Heading>🎵 Start Your Music Journey!</Alert.Heading>
                  <p className="mb-3">
                    We need to learn about your music taste to give you personalized recommendations.
                  </p>
                  <p className="mb-0">
                    <strong>Review some albums or tracks</strong> with 4+ stars and we'll find similar music you'll love!
                  </p>
                </Alert>
              ) : (
                <Row>
                  {/* Top Recommendation */}
                  <Col md={6}>
                    {renderTopRecommendation(shuffledRecommendations[0])}
                  </Col>

                  {/* Scrollable Song Recommendations */}
                  <Col md={6}>
                    <div
                      className="hscroll-wrap"
                      style={{
                        '--gap': '1rem',
                      }}
                    >
                      <div
                        className="fade-edges"
                        style={{
                          '--fade-bg': '#ffffff',
                        }}
                      >
                        <div
                          className="hscroll"
                          ref={scrollerRef}
                          onScroll={updateArrowState}
                          style={{
                            overflowX: 'auto',
                            display: 'flex',
                            scrollSnapType: 'x mandatory',
                            '--fade-rgb': '245,245,245',
                            paddingInline: 'var(--gap)',
                            scrollPaddingInline: 'var(--gap)',
                            gap: '1rem',
                          }}
                        >
                          {/* small song pages */}
                          {[...Array(Math.ceil((shuffledRecommendations.length - 1) / 3))].map((_, pageIndex) => {
                            const start = 1 + pageIndex * 3;
                            const group = shuffledRecommendations.slice(start, start + 3);

                            return (
                              <div
                                key={pageIndex}
                                style={{
                                  minWidth: 'calc(50% - 0.5rem)',
                                  scrollSnapAlign: 'start',
                                }}
                              >
                                <Row className="g-0">
                                  {group.map((track) => (
                                    <Col xs={12} key={track.id} className="mb-3">
                                      {renderSmallRecommendation(track)}
                                    </Col>
                                  ))}
                                </Row>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Pager buttons OUTSIDE the fade-edges */}
                      <div
                        className="mt-2"
                        style={{
                          display: 'flex',
                          gap: '0.1rem',
                          paddingRight: 'var(--gap)',
                          justifyContent: 'flex-end',
                          position: 'relative',
                          top: '-1.5rem',
                        }}
                      >
                        {/* LEFT (<) */}
                        <button
                          type="button"
                          className="pager-btn"
                          onClick={scrollToPrev}
                          disabled={!canPrev}
                          aria-label="Previous"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="13"
                            height="13"
                            viewBox="0 0 8 12"
                            fill="none"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="7,1 1,6 7,11" />
                          </svg>
                        </button>

                        {/* RIGHT (>) */}
                        <button
                          type="button"
                          className="pager-btn"
                          onClick={scrollToNext}
                          disabled={!canNext}
                          aria-label="Next"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="13"
                            height="13"
                            viewBox="0 0 8 12"
                            fill="none"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="1,1 7,6 1,11" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          )}

          {/* Latest Reviews & Top Reviews — always visible */}
          <Row className="mt-5">
            {/* Latest Reviews */}
            <Col md={6}>
              <div style={{ paddingInline: 'var(--gap)' }}>
                <div className="home-section-bar">
                  <h5 className="home-section-title" style={{ textAlign: 'left' }}>
                    {isLoadingLatest || latestReviews.length > 0 ? 'Latest Reviews' : 'New Releases'}
                  </h5>
                  <div />
                </div>

                {isLoadingLatest || (latestReviews.length === 0 && isLoadingNewReleases) ? (
                  <div className="text-center">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : latestReviews.length > 0 ? (
                  <div className="vscroll-wrap">
                    <div className="vscroll">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {latestReviews.map(renderReviewCard)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="vscroll-wrap">
                    <div className="vscroll">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {newReleases.slice(0, 10).map(renderAlbumCard)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Col>

            {/* Top Reviews */}
            <Col md={6}>
              <div style={{ paddingInline: 'var(--gap)' }}>
                <div className="home-section-bar">
                  <h5 className="home-section-title">
                    {isLoadingTop || topReviews.length > 0 ? 'Top Reviews' : 'More New Releases'}
                  </h5>

                  {topReviews.length > 0 && (
                    <div className="d-flex gap-2">
                      {['all', 'month', 'week', 'today'].map((k) => (
                        <button
                          key={k}
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setTopRange(k)}
                          style={{
                            background: topRange === k ? '#000' : 'transparent',
                            color: topRange === k ? '#fff' : '#666',
                            border: '1px solid #ccc',
                            borderRadius: 999,
                            padding: '2px 10px',
                          }}
                        >
                          {k === 'all' ? 'All' : k === 'month' ? 'Past month' : k === 'week' ? 'Past week' : 'Today'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {isLoadingTop || (topReviews.length === 0 && isLoadingNewReleases) ? (
                  <div className="text-center">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : topReviews.length > 0 ? (
                  <div className="vscroll-wrap">
                    <div className="vscroll">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {topReviews.map(renderReviewCard)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="vscroll-wrap">
                    <div className="vscroll">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {newReleasesB.map(renderAlbumCard)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default HomePage;
